/**
 * SortableCategoryRow — single drag-sortable category row used inside
 * `<SortableCategoryList>` (edit mode only).
 *
 * Decisions wired here:
 *   - Decision: Touch sensor split — handle activates drag, body scrolls.
 *     `attributes` + `listeners` from `useSortable` are spread ONLY on the
 *     drag handle `<button>`. The row body (icon, name, system tag,
 *     delete button) does not receive listeners, so touch-on-body falls
 *     through to native scroll.
 *   - Decision: Drop indicator — line between rows via DragOverlay +
 *     transform. We apply `CSS.Transform.toString(transform)` plus
 *     `transition` to the row container so other rows reflow when a peer
 *     is picked up. The lifted item's own visuals (elevation, ghost) live
 *     in the parent list's `<DragOverlay>`.
 *   - Coexists with the existing edit-mode UI: `is_system` "預設" chip,
 *     "−" delete button. The pencil-edit button is intentionally hidden
 *     in edit mode (per design Risks/Trade-offs).
 */

import type { CSSProperties, ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Icon, { type IconName } from '@/components/common/Icon';
import type { CategoryRow } from '@/lib/sheets/types';

export interface SortableCategoryRowProps {
  category: CategoryRow;
  /** Whether this row's parent group is currently inactive (cross-parent
   *  drag in progress). Visually dimmed + aria-disabled. */
  groupDisabled?: boolean;
  /** Render the row body. Caller controls visual layout (icon, name,
   *  system tag, delete button) so we don't duplicate CategoriesScreen's
   *  pixel-faithful styling. The body MUST NOT spread drag listeners. */
  children: ReactNode;
  /** Optional aria-label override for the handle; defaults to
   *  `拖曳排序：{name}`. */
  handleLabel?: string;
}

function tokenToCss(token: string): string {
  if (token.startsWith('--')) return `var(${token})`;
  return token;
}

export default function SortableCategoryRow({
  category,
  groupDisabled = false,
  children,
  handleLabel,
}: SortableCategoryRowProps): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: category.id,
    disabled: groupDisabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: groupDisabled ? 0.5 : isDragging ? 0.4 : 1,
    background: isDragging ? 'var(--bg-raised)' : undefined,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      aria-disabled={groupDisabled || undefined}
      data-testid={`sortable-row-${category.id}`}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
        }}
      >
        {/* Body: icon + name + chips + delete button. Caller renders
            children here. Do NOT spread drag listeners on this region. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          {children}
        </div>
        {/* Drag handle: this is the ONLY element that receives sort
            listeners. Activation distance/delay is configured at the
            DndContext level. */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={handleLabel ?? `拖曳排序：${category.name}`}
          aria-disabled={groupDisabled || undefined}
          tabIndex={0}
          style={{
            width: 36,
            height: 36,
            display: 'grid',
            placeItems: 'center',
            color: 'var(--fg-3)',
            flexShrink: 0,
            background: 'transparent',
            border: 0,
            cursor: groupDisabled ? 'not-allowed' : 'grab',
            touchAction: 'none', // tell browser not to scroll while pressing handle
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="9" cy="6" r="1.6" />
            <circle cx="15" cy="6" r="1.6" />
            <circle cx="9" cy="12" r="1.6" />
            <circle cx="15" cy="12" r="1.6" />
            <circle cx="9" cy="18" r="1.6" />
            <circle cx="15" cy="18" r="1.6" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/** Helper exported for parent screens that want to render the standard
 *  category row body (delete button, color swatch + icon, name + system
 *  chip, sub count) without duplicating styling. */
export interface CategoryRowBodyProps {
  category: CategoryRow;
  subCount?: number | null;
  onDelete?: () => void;
}

export function CategoryRowBody({
  category,
  subCount,
  onDelete,
}: CategoryRowBodyProps): JSX.Element {
  // Raw stored value straight to <Icon value> — it parses lucide:/emoji:
  // prefixes and bare legacy names itself, with its own tag fallback.
  // (A local whitelist here once swallowed every prefixed value → tag.)
  const Ic = (category.icon || 'tag') as IconName;
  return (
    <>
      {!category.is_system && onDelete ? (
        <button
          type="button"
          aria-label={`刪除 ${category.name}`}
          onClick={onDelete}
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            background: 'var(--clay-500)',
            border: 0,
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="minus" size={14} stroke="#fff" />
        </button>
      ) : (
        <span aria-hidden="true" style={{ width: 22, height: 22, flexShrink: 0 }} />
      )}
      <div
        aria-hidden="true"
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: tokenToCss(category.color_token),
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        <Icon value={Ic} size={18} stroke="#fff" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            font: '500 15px var(--font-cjk)',
            color: 'var(--fg-1)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {category.name}
          {category.is_system && (
            <span
              style={{
                font: '500 10px var(--font-cjk)',
                color: 'var(--matcha-700)',
                background: 'rgba(126,139,90,0.14)',
                padding: '1px 6px',
                borderRadius: 999,
              }}
            >
              預設
            </span>
          )}
        </div>
        {subCount != null && (
          <div
            style={{
              font: '400 12px var(--font-cjk)',
              color: 'var(--fg-3)',
              marginTop: 2,
            }}
          >
            {subCount} 個子分類
          </div>
        )}
      </div>
    </>
  );
}
